import { useState, useEffect } from "react";
import { Select, Modal } from "antd";
import { useOutletContext } from "react-router-dom";
import {
  users,
  departments,
  roles,
  jobTypes,
  workStages,
  type UserItem,
  type DepartmentItem,
  type RoleItem,
  type JobTypeItem,
  type PasswordResetRequestItem,
} from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import RIcon from "../../../../components/RIcon";

const roleStyle: Record<string, string> = {
  superuser: "bg-purple-100 text-purple-700",
  default: "bg-gray-100 text-gray-600",
};

export default function UsersPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [userList, setUserList] = useState<UserItem[]>([]);
  const [deptList, setDeptList] = useState<DepartmentItem[]>([]);
  const [roleList, setRoleList] = useState<RoleItem[]>([]);
  const [jobTypeList, setJobTypeList] = useState<JobTypeItem[]>([]);
  const [workStageList, setWorkStageList] = useState<{ id: number; name: string; job_type_id?: number }[]>([]);
  const [resetRequests, setResetRequests] = useState<PasswordResetRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState<UserItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState<UserItem | null>(null);
  const [resetPwdValue, setResetPwdValue] = useState("");
  const [resetting, setResetting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    full_name: "",
    employee_id: "",
    email: "",
    department_id: "" as string | number,
    job_type_id: "" as string | number,
    role_id: "" as string | number,
    work_stage_id: "" as string | number,
  });

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleResetPassword = async () => {
    if (!ensurePermission(user, "users:write", (msg) => showToast(msg, "error"))) return;
    if (!resetPwdUser) return;
    if (!resetPwdValue || resetPwdValue.length < 8) {
      showToast("密码至少8位", "error");
      return;
    }
    setResetting(true);
    try {
      await users.resetPassword(resetPwdUser.id, resetPwdValue);
      setResetPwdUser(null);
      setResetPwdValue("");
      showToast("密码重置成功");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "密码重置失败", "error");
    } finally {
      setResetting(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await users.list();
      setUserList(data);
    } catch {
      showToast("加载用户列表失败", "error");
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [userData, deptData, roleData, jobTypeData, workStageData, resetRequestData] = await Promise.all([
          users.list(),
          departments.list(),
          roles.list(),
          jobTypes.list(),
          workStages.list().catch(() => []),
          users.listPasswordResetRequests().catch(() => []),
        ]);
        setUserList(userData);
        setDeptList(deptData);
        setRoleList(roleData);
        setJobTypeList(jobTypeData);
        setWorkStageList(workStageData);
        setResetRequests((resetRequestData || []).filter((item) => item.status === "pending"));
      } catch {
        showToast("加载数据失败", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async (targetUser: UserItem) => {
    if (!ensurePermission(user, "users:write", (msg) => showToast(msg, "error"))) return;
    try {
      await users.update(targetUser.id, { is_active: !targetUser.is_active });
      await loadUsers();
      showToast("操作成功");
    } catch {
      showToast("操作失败", "error");
    }
  };

  const handleDelete = async (id: number) => {
    if (!ensurePermission(user, "users:delete", (msg) => showToast(msg, "error"))) return;
    try {
      await users.delete(id);
      setDeleteId(null);
      await loadUsers();
      showToast("用户已删除");
    } catch {
      showToast("删除失败", "error");
    }
  };

  const handleSaveEdit = async () => {
    if (!ensurePermission(user, "users:write", (msg) => showToast(msg, "error"))) return;
    if (!editUser) return;
    setSaving(true);
    try {
      await users.update(editUser.id, {
        email: editUser.email,
        full_name: editUser.full_name,
        employee_id: editUser.employee_id,
        is_active: editUser.is_active,
        department_id: editUser.department_id,
        job_type_id: editUser.job_type_id,
        role_id: editUser.role_id,
        work_stage_id: editUser.work_stage_id,
      });
      setEditUser(null);
      await loadUsers();
      showToast("用户信息已更新");
    } catch {
      showToast("保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!ensurePermission(user, "users:write", (msg) => showToast(msg, "error"))) return;
    if (!newUser.username || !newUser.password) {
      showToast("用户名和密码为必填项", "error");
      return;
    }
    if (newUser.password.length < 8) {
      showToast("密码至少8位", "error");
      return;
    }
    setCreating(true);
    try {
      await users.create({
        username: newUser.username,
        password: newUser.password,
        full_name: newUser.full_name || undefined,
        employee_id: newUser.employee_id || undefined,
        email: newUser.email || undefined,
        department_id: newUser.department_id ? Number(newUser.department_id) : undefined,
        job_type_id: newUser.job_type_id ? Number(newUser.job_type_id) : undefined,
        role_id: newUser.role_id ? Number(newUser.role_id) : undefined,
        work_stage_id: newUser.work_stage_id ? Number(newUser.work_stage_id) : undefined,
      });
      setCreateOpen(false);
      setNewUser({ username: "", password: "", full_name: "", employee_id: "", email: "", department_id: "", job_type_id: "", role_id: "", work_stage_id: "" });
      await loadUsers();
      showToast("用户创建成功");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "创建用户失败", "error");
    } finally {
      setCreating(false);
    }
  };

  const TableRow = ({ rowUser }: { rowUser: UserItem }) => (
    <tr className="border-b border-gray-50 hover:bg-[#F4F8FB]/50 transition-colors">
      <td className="px-4 py-3 font-mono text-xs text-[#718096]">{rowUser.username}</td>
      <td className="px-4 py-3 font-mono text-xs text-[#4A5568] whitespace-nowrap">{rowUser.employee_id || "-"}</td>
      <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{rowUser.email || "-"}</td>
      <td className="px-4 py-3 font-medium text-[#1A2B3C] whitespace-nowrap">{rowUser.full_name || "-"}</td>
      <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{rowUser.department_name || "-"}</td>
      <td className="px-4 py-3 text-[#4A5568] text-xs whitespace-nowrap">{rowUser.job_type_name || "-"}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${rowUser.is_superuser ? roleStyle.superuser : roleStyle.default}`}>
          {rowUser.role_name || (rowUser.is_superuser ? "超级管理员" : "未分配")}
        </span>
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => handleToggle(rowUser)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
            rowUser.is_active ? "bg-[#0EA5A0]" : "bg-gray-300"
          }`}
        >
          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${rowUser.is_active ? "translate-x-4.5" : "translate-x-0.5"}`} />
        </button>
      </td>
      <td className="px-4 py-3 text-[#718096] text-xs whitespace-nowrap">{rowUser.created_at?.slice(0, 10) || "-"}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button onClick={() => ensurePermission(user, "users:write", (msg) => showToast(msg, "error")) && setEditUser(rowUser)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] cursor-pointer">
            <RIcon name="edit-line" className="text-sm" />
          </button>
          <button onClick={() => ensurePermission(user, "users:write", (msg) => showToast(msg, "error")) && setResetPwdUser(rowUser)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] cursor-pointer" title="重置密码">
            <RIcon name="key-line" className="text-sm" />
          </button>
          <button onClick={() => ensurePermission(user, "users:delete", (msg) => showToast(msg, "error")) && setDeleteId(rowUser.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 cursor-pointer">
            <RIcon name="delete-bin-line" className="text-sm" />
          </button>
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RIcon name="loader-4-line" className="animate-spin text-2xl text-[#0EA5A0]" />
        <span className="ml-2 text-[#718096]">加载中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] text-white text-sm px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2 ${
          toast.type === "error" ? "bg-red-600" : "bg-[#1A2B3C]"
        }`}>
          <i className={toast.type === "error" ? "ri-error-warning-line text-yellow-300" : "ri-checkbox-circle-line text-[#AAFF45]"} />
          {toast.msg}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">用户管理</h2>
          <p className="text-xs text-[#718096] mt-0.5">管理系统用户账号、权限和状态</p>
        </div>
        <button
          onClick={() => ensurePermission(user, "users:write", (msg) => showToast(msg, "error")) && setCreateOpen(true)}
          className="h-9 px-4 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap w-full sm:w-auto"
        >
          <RIcon name="add-line" />
          新建用户
        </button>
      </div>

      {/* 注册申请：待管理员审核启用的新注册账号 */}
      {(
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-[#1A2B3C]">用户注册申请</h3>
              <button onClick={() => loadUsers().then(() => showToast("已刷新"))} className="h-7 px-2.5 rounded-lg border border-gray-200 text-xs text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] cursor-pointer whitespace-nowrap">刷新</button>
            </div>
          </div>
          {(() => {
            // 注册申请：仅显示从未审核过的（管理员手动停用的不在这里）
            const pending = userList.filter((u) => !u.is_active && !u.approved_at);
            if (pending.length === 0) return <div className="px-4 py-6 text-xs text-[#718096]">当前没有待审核的注册申请</div>;
            return (
              <div className="divide-y divide-gray-100">
                {pending.map((u) => (
                  <div key={u.id} className="px-4 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 rounded-full bg-[#F4F8FB] flex items-center justify-center flex-shrink-0">
                        <RIcon name="user-line" className="text-[#0EA5A0] text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1A2B3C]">
                          {u.full_name || u.username}
                          <span className="ml-2 font-mono text-xs text-[#718096]">@{u.username}</span>
                        </p>
                        <p className="text-xs text-[#718096] truncate">
                          工号 {u.employee_id || "-"} · {u.email || "无邮箱"} · 部门 {u.department_name || "-"} · 工种 {u.job_type_name || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-[#718096]">{u.created_at?.slice(0, 16).replace("T", " ")}</span>
                      <button
                        onClick={async () => {
                          if (!ensurePermission(user, "users:write", (msg) => showToast(msg, "error"))) return;
                          try {
                            await users.update(u.id, { is_active: true });
                            await loadUsers();
                            showToast(`已启用账号「${u.username}」`);
                          } catch {
                            showToast("启用失败", "error");
                          }
                        }}
                        className="h-8 px-3 rounded-lg bg-[#0EA5A0] text-white text-xs hover:bg-[#0D9590] cursor-pointer whitespace-nowrap"
                      >
                        审核启用
                      </button>
                      <button
                        onClick={() => {
                          if (!ensurePermission(user, "users:write", (msg) => showToast(msg, "error"))) return;
                          Modal.confirm({
                            title: "确认拒绝注册申请",
                            content: `账号「${u.username}」会保留但不可登录，需要彻底清除请到下方用户列表里手动删除。`,
                            okText: "拒绝",
                            okType: "danger",
                            cancelText: "取消",
                            centered: true,
                            onOk: async () => {
                              try {
                                await users.reject(u.id);
                                await loadUsers();
                                showToast("已拒绝该注册申请");
                              } catch {
                                showToast("操作失败", "error");
                              }
                            },
                          });
                        }}
                        className="h-8 px-3 rounded-lg border border-gray-200 text-[#718096] text-xs hover:border-red-400 hover:text-red-500 cursor-pointer whitespace-nowrap"
                      >
                        拒绝
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* 密码重置申请已下线（用户走邮箱验证码自助重置） */}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                {["用户名", "工号", "邮箱", "真实姓名", "部门", "工种", "角色", "状态", "创建时间", "操作"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {userList.map((u) => <TableRow key={u.id} rowUser={u} />)}
            </tbody>
          </table>
          {userList.length === 0 && (
            <div className="text-center py-10 text-[#718096] text-sm">暂无用户数据</div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑用户</h3>
              <button onClick={() => setEditUser(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">真实姓名</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={editUser.full_name || ""}
                  onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">工号</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  placeholder="如 R0001"
                  value={editUser.employee_id || ""}
                  onChange={(e) => setEditUser({ ...editUser, employee_id: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">邮箱</label>
                <input
                  type="email"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={editUser.email || ""}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门</label>
                <Select
                  showSearch allowClear placeholder="未分配" style={{ width: "100%" }}
                  value={editUser.department_id ?? undefined}
                  onChange={(v) => setEditUser({ ...editUser, department_id: (v as number | undefined) ?? null })}
                  optionFilterProp="label"
                  options={deptList.map((d) => ({ value: d.id, label: d.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">工种</label>
                <Select
                  showSearch allowClear placeholder="未分配" style={{ width: "100%" }}
                  value={editUser.job_type_id ?? undefined}
                  onChange={(v) => setEditUser({ ...editUser, job_type_id: (v as number | undefined) ?? null })}
                  optionFilterProp="label"
                  options={jobTypeList.map((j) => ({ value: j.id, label: j.name }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">角色</label>
                <Select
                  showSearch allowClear placeholder="未分配" style={{ width: "100%" }}
                  value={editUser.role_id ?? undefined}
                  onChange={(v) => setEditUser({ ...editUser, role_id: (v as number | undefined) ?? null })}
                  optionFilterProp="label"
                  options={roleList.map((r) => ({ value: r.id, label: r.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">状态</label>
                <Select
                  style={{ width: "100%" }}
                  value={editUser.is_active ? "1" : "0"}
                  onChange={(v) => setEditUser({ ...editUser, is_active: v === "1" })}
                  options={[
                    { value: "1", label: "启用" },
                    { value: "0", label: "禁用" },
                  ]}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditUser(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >{saving ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存修改"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">新建用户</h3>
              <button onClick={() => setCreateOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">用户名 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="请输入用户名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">密码 <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="请输入密码"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">真实姓名</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="请输入真实姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">邮箱</label>
                <input
                  type="email"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="请输入邮箱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门</label>
                <Select
                  showSearch allowClear placeholder="未分配" style={{ width: "100%" }}
                  value={newUser.department_id || undefined}
                  onChange={(v) => setNewUser({ ...newUser, department_id: (v ?? "") as string })}
                  optionFilterProp="label"
                  options={deptList.map((d) => ({ value: String(d.id), label: d.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">工种</label>
                <Select
                  showSearch allowClear placeholder="未分配" style={{ width: "100%" }}
                  value={newUser.job_type_id || undefined}
                  onChange={(v) => setNewUser({ ...newUser, job_type_id: (v ?? "") as string })}
                  optionFilterProp="label"
                  options={jobTypeList.map((j) => ({ value: String(j.id), label: j.name }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">角色</label>
                <Select
                  showSearch allowClear placeholder="未分配" style={{ width: "100%" }}
                  value={newUser.role_id || undefined}
                  onChange={(v) => setNewUser({ ...newUser, role_id: (v ?? "") as string })}
                  optionFilterProp="label"
                  options={roleList.map((r) => ({ value: String(r.id), label: r.name }))}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setCreateOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >{creating ? <><RIcon name="loader-4-line" className="animate-spin" />创建中...</> : "创建用户"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RIcon name="delete-bin-line" className="text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-[#1A2B3C] mb-1">确认删除用户</h3>
            <p className="text-sm text-[#718096] mb-6">此操作不可撤销，用户数据将被永久删除</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwdUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">重置密码</h3>
              <button onClick={() => { setResetPwdUser(null); setResetPwdValue(""); }} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <p className="text-sm text-[#718096]">
                为用户 <span className="font-medium text-[#1A2B3C]">{resetPwdUser.username}</span> 设置新密码
              </p>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">新密码 <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={resetPwdValue}
                  onChange={(e) => setResetPwdValue(e.target.value)}
                  placeholder="请输入新密码（至少8位）"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setResetPwdUser(null); setResetPwdValue(""); }} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={handleResetPassword}
                  disabled={resetting}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >{resetting ? <><RIcon name="loader-4-line" className="animate-spin" />重置中...</> : "确认重置"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
